// `expand` pseudo-statement: a lightweight batch-generation DSL for the query
// editor (both the regular Query tab and Fleet Query).
//
//   expand src="SELECT NoJual, Divisi FROM tr_jual_m WHERE Tgl >= @TglStart",
//          target="CALL ProsesJurnal(&NoJual, &Divisi);"
//
// Runs `src` as an ordinary query, then for EACH row emits one copy of `target`
// with every `&ColumnName` token replaced by that row's value for that column
// (quoted + escaped as a safe SQL literal, or NULL, or bare for a numeric
// column) — turning one `expand` statement into N real statements that then run
// exactly like any other statement in the batch.
//
// This module is pure aside from the injected `runSrcQuery` callback (the two
// panels wire it to their own session-execute call), so the parsing/escaping/
// substitution logic is identical — and independently testable — in both.

import { isNumericType } from './format.js';

const EXPAND_RE = /^expand\b/i;

// isExpandStatement: cheap check on a split statement's text.
export function isExpandStatement(text) {
  return EXPAND_RE.test(text.trim());
}

// Extracts a quoted attribute value: NAME = "..." or NAME = '...', with
// backslash-escaping of the delimiter (and \\) inside — the common convention,
// distinct from MySQL's own '' / "" doubling (this is DSL-attribute syntax, not
// SQL-string syntax; the src/target VALUES are themselves raw SQL text).
function extractAttr(text, name) {
  const re = new RegExp(name + String.raw`\s*=\s*(["'])((?:\\.|(?!\1).)*)\1`, 'i');
  const m = re.exec(text);
  if (!m) return null;
  const quote = m[1];
  return m[2].replace(new RegExp('\\\\' + quote, 'g'), quote).replace(/\\\\/g, '\\');
}

// parseExpandStatement: { src, target } or throws a clear Error naming what's
// missing. Caller should only call this after isExpandStatement(text) is true.
export function parseExpandStatement(text) {
  const src = extractAttr(text, 'src');
  const target = extractAttr(text, 'target');
  const missing = [];
  if (src == null) missing.push('src="…"');
  if (target == null) missing.push('target="…"');
  if (missing.length) {
    throw new Error(`expand: missing or malformed ${missing.join(' and ')} — expected: expand src="SELECT …", target="… &Col …"`);
  }
  return { src, target };
}

// escapeSqlString: MySQL string-literal escaping (backslash is the escape
// character by default — NO_BACKSLASH_ESCAPES is not assumed off). Values are
// always emitted single-quoted regardless of the target template's own quoting
// style, since a double-quoted literal is misread as an IDENTIFIER under
// ANSI_QUOTES sql_mode — single quotes are unambiguous on every MySQL/MariaDB
// version and mode.
export function escapeSqlString(value) {
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\0/g, '\\0')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\x1a/g, '\\Z');
}

// formatSqlValue: NULL keyword for null, bare text for a numeric column
// (already the correct literal text), a safely-escaped quoted string otherwise.
function formatSqlValue(value, colType) {
  if (value === null || value === undefined) return 'NULL';
  if (isNumericType(colType)) return String(value);
  return "'" + escapeSqlString(value) + "'";
}

// Every &ColumnName token in `target`, matched case-insensitively against the
// src result's columns so the user doesn't have to remember exact casing.
const MACRO_RE = /&([A-Za-z_][A-Za-z0-9_]*)/g;

// validateMacros: throws once, up front, naming every &Col that doesn't match a
// src column — so a typo fails the whole expand before anything runs, rather
// than silently leaving "&Typo" in every generated statement.
function validateMacros(target, columns) {
  const names = new Set(columns.map((c) => c.name.toLowerCase()));
  const unknown = new Set();
  for (const m of target.matchAll(MACRO_RE)) {
    if (!names.has(m[1].toLowerCase())) unknown.add(m[1]);
  }
  if (unknown.size > 0) {
    const have = columns.map((c) => c.name).join(', ') || '(none)';
    throw new Error(`expand: target references &${[...unknown].join(', &')} — src's columns are: ${have}`);
  }
}

// expandRowStatement: substitute one row's values into the target template.
function expandRowStatement(target, columns, row) {
  const byName = new Map(columns.map((c, i) => [c.name.toLowerCase(), i]));
  return target.replace(MACRO_RE, (whole, col) => {
    const i = byName.get(col.toLowerCase());
    return formatSqlValue(row[i], columns[i].type);
  });
}

// expandOne: runs src (via the injected runner), validates, and returns the N
// generated statement texts (possibly 0, if src returned no rows — not an
// error). runSrcQuery(sql) => Promise<{ columns: [{name,type}], rows: [[...]] }>
// (or throws / returns an error string on failure — either propagates).
export async function expandOne(text, runSrcQuery) {
  const { src, target } = parseExpandStatement(text);
  const result = await runSrcQuery(src);
  if (result.error) throw new Error(`expand: src query failed — ${result.error}`);
  const columns = result.columns || [];
  const rows = result.rows || [];
  validateMacros(target, columns);
  return rows.map((row) => expandRowStatement(target, columns, row));
}

// expandStatements: maps an array of split statements — { text, ... } — to a
// new array, replacing each `expand` entry with its N generated statements
// (every other field on the original entry is preserved onto each generated
// one, so callers that route on other properties, e.g. fleet's per-statement
// [connection]/target, keep working unchanged). runSrcQuery is called once per
// `expand` entry found. Statements are processed in order so an earlier
// expand's error surfaces before later statements are attempted.
export async function expandStatements(stmts, runSrcQuery) {
  const out = [];
  for (const s of stmts) {
    if (!isExpandStatement(s.text)) { out.push(s); continue; }
    const generated = await expandOne(s.text, runSrcQuery);
    for (const text of generated) out.push({ ...s, text });
  }
  return out;
}
